/** Project structure setup types (operations) and matching review types (management). */
export const PROJECT_SETUP_TYPES = {
  ACCOMMODATION: 'Project Setup Accommodation',
  ACCOMMODATION_PRICING: 'Project Setup Accommodation Pricing',
  BLOCK: 'Project Setup Block',
  UNIT: 'Project Setup Unit',
  PLC_AND_EXTRA_CHARGES: 'Project Setup PLC And Extra Charges'
};

const REVIEW_SUFFIX = ' Review';

export const PROJECT_SETUP_REVIEW_TYPES = Object.fromEntries(
  Object.entries(PROJECT_SETUP_TYPES).map(([key, setupType]) => [
    key,
    `${setupType}${REVIEW_SUFFIX}`
  ])
);

export const ALL_PROJECT_SETUP_TYPES = Object.values(PROJECT_SETUP_TYPES);
export const ALL_PROJECT_SETUP_REVIEW_TYPES = Object.values(PROJECT_SETUP_REVIEW_TYPES);

export function toProjectSetupReviewType(setupType) {
  if (!setupType || isProjectSetupReviewType(setupType)) return setupType;
  if (isProjectSetupType(setupType)) return `${setupType}${REVIEW_SUFFIX}`;
  return null;
}

export function toProjectSetupType(reviewOrSetupType) {
  if (!reviewOrSetupType) return null;
  if (isProjectSetupType(reviewOrSetupType)) return reviewOrSetupType;
  if (isProjectSetupReviewType(reviewOrSetupType)) {
    return reviewOrSetupType.replace(REVIEW_SUFFIX, '');
  }
  return null;
}

export function isProjectSetupType(type) {
  return ALL_PROJECT_SETUP_TYPES.includes(type);
}

export function isProjectSetupReviewType(type) {
  return (
    ALL_PROJECT_SETUP_REVIEW_TYPES.includes(type) ||
    (typeof type === 'string' &&
      type.startsWith('Project Setup ') &&
      type.endsWith(REVIEW_SUFFIX))
  );
}

/** Pick management mid (Medium), else primary (High). Skips inactive users. */
export function resolveManagementReviewer(users = []) {
  const managementUsers = users.filter(
    (u) =>
      (u?.vertical === 'Management' || u?.designation?.vertical === 'Management') &&
      u?.is_active !== false &&
      u?.is_blocked !== true
  );

  const mid = managementUsers.find((u) => u?.level === 'Medium');
  if (mid) return { user: mid, role: 'mid' };

  const primary = managementUsers.find((u) => u?.level === 'High');
  if (primary) return { user: primary, role: 'primary' };

  return { user: null, role: null };
}

/**
 * Navigation target for setup/review priorities on project details.
 * Returns { path, state, localStorageTab } or null.
 */
export function getProjectSetupNavigation(projectId, priorityType) {
  const setupType = toProjectSetupType(priorityType);
  if (!setupType || !projectId) return null;

  const basePath = `/builder/management/company/projects/${projectId}`;

  switch (setupType) {
    case PROJECT_SETUP_TYPES.ACCOMMODATION:
      return {
        path: basePath,
        state: { secondaryTab: 'Accommodation' },
        localStorageTab: 'Accommodation'
      };
    case PROJECT_SETUP_TYPES.ACCOMMODATION_PRICING:
      return {
        path: basePath,
        state: { secondaryTab: 'Pricing', pricingSubTab: 'Accommodation Pricing' },
        localStorageTab: 'Accommodation Pricing'
      };
    case PROJECT_SETUP_TYPES.BLOCK:
    case PROJECT_SETUP_TYPES.UNIT:
      return {
        path: basePath,
        state: { secondaryTab: 'Block' },
        localStorageTab: 'Block'
      };
    case PROJECT_SETUP_TYPES.PLC_AND_EXTRA_CHARGES:
      return {
        path: basePath,
        state: { secondaryTab: 'Pricing', pricingSubTab: 'PLC' },
        localStorageTab: 'PLC'
      };
    default:
      return null;
  }
}

/** Maps management onboarding-map step ids to project setup navigation. */
const MANAGEMENT_ONBOARDING_STEP_SETUP = {
  accommodation_setup: PROJECT_SETUP_TYPES.ACCOMMODATION,
  accommodation: PROJECT_SETUP_TYPES.ACCOMMODATION,
  block_setup: PROJECT_SETUP_TYPES.BLOCK,
  block: PROJECT_SETUP_TYPES.BLOCK,
  unit_setup: PROJECT_SETUP_TYPES.UNIT,
  unit: PROJECT_SETUP_TYPES.UNIT,
  pricing_setup: PROJECT_SETUP_TYPES.ACCOMMODATION_PRICING,
  pricing: PROJECT_SETUP_TYPES.ACCOMMODATION_PRICING,
  plc_extra_charges: PROJECT_SETUP_TYPES.PLC_AND_EXTRA_CHARGES,
  plc_and_extra_charges: PROJECT_SETUP_TYPES.PLC_AND_EXTRA_CHARGES
};

const BUILDER_ONBOARDING_STEP_ROUTES = {
  company: '/builder/management/company',
  project: '/builder/management/company',
  team_setup: '/builder/users'
};

export function getManagementOnboardingStepNavigation(projectId, stepId) {
  if (!stepId) return null;

  const setupType = MANAGEMENT_ONBOARDING_STEP_SETUP[stepId];
  if (setupType && projectId) {
    return getProjectSetupNavigation(projectId, setupType);
  }

  const builderPath = BUILDER_ONBOARDING_STEP_ROUTES[stepId];
  if (builderPath) {
    return {
      path: builderPath,
      state: projectId ? { projectID: projectId } : undefined,
      localStorageTab: null
    };
  }

  return null;
}

export const MANAGEMENT_ONBOARDING_STEP_ACTION_LABELS = {
  company: 'Set up company',
  project: 'Create project',
  team_setup: 'Set up team',
  accommodation_setup: 'Set up accommodation',
  accommodation: 'Set up accommodation',
  block_setup: 'Set up blocks',
  block: 'Set up blocks',
  unit_setup: 'Set up units',
  unit: 'Set up units',
  pricing_setup: 'Set up pricing',
  pricing: 'Set up pricing',
  plc_extra_charges: 'Configure PLC & charges',
  plc_and_extra_charges: 'Configure PLC & charges'
};

export function getProjectSetupApprovalLabel(priorityType) {
  const setupType = toProjectSetupType(priorityType);
  if (!setupType) return priorityType;
  const labels = {
    [PROJECT_SETUP_TYPES.ACCOMMODATION]: 'Review accommodation setup',
    [PROJECT_SETUP_TYPES.ACCOMMODATION_PRICING]: 'Review accommodation pricing',
    [PROJECT_SETUP_TYPES.BLOCK]: 'Review block setup',
    [PROJECT_SETUP_TYPES.UNIT]: 'Review unit setup',
    [PROJECT_SETUP_TYPES.PLC_AND_EXTRA_CHARGES]: 'Review PLC & extra charges'
  };
  return labels[setupType] || `Review ${setupType}`;
}
